import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAdminClientList } from '@/hooks/useAdmin'
import { useUIStore } from '@/store/ui'
import { Logo } from '@/components/brand/Logo'
import { IconMenu, IconX, IconBrief, IconSliders } from '@/components/brand/icons'
import '@/styles/admin.css'

export function AdminShell() {
  const [menuOpen, setMenuOpen] = useState(false)
  const { data: clients } = useAdminClientList()
  const setViewingAs = useUIStore((s) => s.setViewingAs)
  const navigate = useNavigate()

  const close = () => setMenuOpen(false)

  async function signOut() {
    await supabase.auth.signOut()
  }

  function enterPortal(client: NonNullable<typeof clients>[number]) {
    setViewingAs(client)
    navigate('/app')
  }

  return (
    <div className={`admin-shell ${menuOpen ? 'is-open' : ''}`}>
      <button className="sidebar-scrim" aria-label="Close menu" onClick={close} />

      <aside className="admin-sidebar">
        <div className="admin-sidebar__header">
          <div className="admin-sidebar__brand">
            <Logo size="sm" />
            <span className="admin-badge">Super Admin</span>
          </div>
          <button className="sidebar-close" aria-label="Close menu" onClick={close}>
            <IconX size={18} />
          </button>
        </div>

        <nav className="admin-nav">
          <NavLink
            to="/admin"
            end
            onClick={close}
            className={({ isActive }) => `admin-nav__item ${isActive ? 'active' : ''}`}
          >
            <IconBrief size={16} /> Dashboard
          </NavLink>
          <NavLink
            to="/admin/settings"
            onClick={close}
            className={({ isActive }) => `admin-nav__item ${isActive ? 'active' : ''}`}
          >
            <IconSliders size={16} /> Settings
          </NavLink>
          <NavLink
            to="/admin/affiliates"
            onClick={close}
            className={({ isActive }) => `admin-nav__item ${isActive ? 'active' : ''}`}
          >
            <IconBrief size={16} /> Affiliates
          </NavLink>
          <NavLink
            to="/admin/ops"
            onClick={close}
            className={({ isActive }) => `admin-nav__item ${isActive ? 'active' : ''}`}
          >
            <IconSliders size={16} /> Ops
          </NavLink>
        </nav>

        {clients && clients.length > 0 && (
          <div className="admin-nav__section">
            <span className="admin-nav__section-label">Clients</span>
            {clients.map((client) => (
              <div key={client.id} className="admin-client-nav">
                <NavLink
                  to={`/admin/client/${client.id}`}
                  onClick={close}
                  className={({ isActive }) => `admin-nav__item admin-nav__item--client ${isActive ? 'active' : ''}`}
                >
                  <span className={`admin-client-dot ${client.active ? 'admin-client-dot--active' : ''}`} />
                  {client.display_name}
                </NavLink>
                <button
                  className="admin-enter-btn"
                  title={`Enter ${client.display_name} portal`}
                  onClick={() => { close(); enterPortal(client) }}
                >
                  →
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="admin-sidebar__footer">
          <NavLink
            to="/app"
            className="admin-nav__item"
            onClick={() => { setViewingAs(null) }}
          >
            <IconSliders size={16} /> User Portal
          </NavLink>
          <button className="sign-out-btn" onClick={() => { void signOut() }}>
            Sign out
          </button>
        </div>
      </aside>

      <div className="admin-body">
        <header className="admin-topbar">
          <button className="topbar-menu" aria-label="Open menu" onClick={() => setMenuOpen(true)}>
            <IconMenu size={20} />
          </button>
          <Logo size="sm" />
          <span className="admin-badge admin-badge--topbar">Super Admin</span>
        </header>

        <main className="admin-content">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
